// Admin portal route path constants. Import via `@app/router/routes`.
// Modules import this as `from '@app/router/routes'`.
// The `src/core/config/routes.ts` file is a re-export shim for older callers.

export const routes = {
  // Auth
  LOGIN: '/login',
  VERIFY_OTP: '/verify-otp',

  // Core
  DASHBOARD: '/dashboard',

  // Property
  PROPERTIES: '/properties',
  // Create wizard (fresh) + resume an existing draft (?step=N). Per
  // property-lifecycle-plan.md §8 URL conventions. ADD_PROPERTY / EDIT_PROPERTY
  // keep their constant names so existing imports don't break; only the paths
  // moved to the wizard convention.
  ADD_PROPERTY: '/properties/create',
  EDIT_PROPERTY: '/properties/:id/create',
  ARCHIVED_PROPERTIES: '/properties/archived',
  PROPERTY_DETAILS: '/properties/:id',
  PROPERTY_QUICK_DETAILS: '/properties/:id/quick-view',
  // Full-page financials add/edit (property-list-detail-plan.md §B.9 — large
  // ~30-field form, full page rather than a drawer). Reached from the
  // Financials tab's "Edit financials" action.
  PROPERTY_FINANCIALS_EDIT: '/properties/:id/financials',
  // Ops-enrichment wizard — guided highlights → amenities → documents → media
  // flow reached from the Enrichment queue's "Enrich" action.
  PROPERTY_ENRICHMENT_WIZARD: '/properties/:id/enrich',
  OFFERING_PROPERTY: '/offering-property',
  OFFERING_CONFIGURE: '/offering-property/configure',
  PROPERTY_OFFERING_FORM: '/offering-property/:id',

  // Offerings (filtered views)
  OFFERINGS: '/offerings',
  OFFERINGS_DRAFTS: '/offerings/drafts',
  OFFERINGS_PENDING_APPROVAL: '/offerings/pending-approval',
  OFFERINGS_VASP_ONBOARDING: '/offerings/vasp-onboarding',
  OFFERINGS_LIVE: '/offerings/live',
  OFFERINGS_FULLY_FUNDED: '/offerings/fully-funded',
  OFFERINGS_ALL: '/offerings/all',
  OFFERINGS_REJECTED: '/offerings/rejected',
  // Read-only single-offering detail (drill-in from the property Overview tab
  // and the offerings queues). `:id` is the OFFERING id, not the property id.
  OFFERING_DETAILS: '/offerings/:id/details',

  // Investor
  INVESTOR: '/investor',
  INVESTOR_DETAILS: '/investor-details/:id',
  INVESTOR_OVERVIEW: '/investor-overview',

  // Orders
  ORDERS: '/orders',
  ORDERS_PENDING_APPROVAL: '/orders/pending-approval',
  ORDERS_ALL: '/orders/all',
  ORDERS_ALL_DETAIL: '/orders/all/:id',
  ORDERS_COMPLETED: '/orders/completed',
  ORDERS_COMPLETED_DETAIL: '/orders/completed/:id',
  ORDERS_REJECTED: '/orders/rejected',
  ORDERS_REJECTED_DETAIL: '/orders/rejected/:id',
  ORDERS_VASP_PROCESSING: '/orders/vasp-processing',
  ORDERS_REFUND: '/orders/refund',
  TOKENISATION: '/tokenisation',

  // Marketplace (secondary market — FSD 4.13)
  MARKETPLACE: '/marketplace',
  MARKETPLACE_LISTINGS: '/marketplace/listings',
  MARKETPLACE_LISTING_DETAIL: '/marketplace/listings/:id',
  MARKETPLACE_TRADES: '/marketplace/trades',
  MARKETPLACE_TRADES_STUCK: '/marketplace/trades/stuck',
  MARKETPLACE_TRADES_RECONCILIATION: '/marketplace/trades/reconciliation',
  MARKETPLACE_TRADE_DETAIL: '/marketplace/trades/:id',
  MARKETPLACE_CONTROLS: '/marketplace/controls',
  MARKETPLACE_PROPERTY_TRADING: '/marketplace/controls/properties/:id',

  // Property Owner Management
  PROPERTY_OWNER_APPROVALS: '/property-owner-approvals',
  PROPERTY_OWNER_DETAILS: '/property-owners/:id',

  // Platform
  ADMINS: '/admins',

  // Settings
  SETTING: '/setting',
  SETTINGS: '/settings',
  SETTINGS_GENERAL: '/settings/general',
  SETTINGS_NOTIFICATIONS: '/settings/notifications',
  SETTINGS_ROLES: '/settings/roles-permissions',
  SETTINGS_AUDIT_LOGS: '/settings/audit-logs',
  SETTINGS_SYSTEM_CONFIG: '/settings/system-configuration',
  // Settings → Integrations → VAISP (Ctrl Alt). AppSidebar and AppTopNav have
  // both pointed at this path since the VAISP work started; the page behind it
  // lives in `@modules/vasp` (failed inbound webhook events + replay).
  SETTINGS_VAISP_CTRL_ALT: '/settings/ctrlalt',
  // Global fee definitions (`fees_master`). Per-property overrides live on the
  // property's own financials screen, not here.
  SETTINGS_FEE_CONFIGURATION: '/settings/fee-configuration',

  // Agreement wording (`agreement_templates`) — the clause bodies every generated
  // MOU, Seller Agreement and window extension is rendered from. Insert-only: a
  // wording change is a new version row, never an edit of a live one.
  //
  // Surfaces under Settings → Platform in the nav but lives under `/masters`,
  // beside the other masters CRUD screens, because that is where its route is
  // registered (`@modules/masters/agreementTemplates`). Do not "tidy" the path to
  // `/settings/…` — the module boundary is the reason, not the URL.
  AGREEMENT_TEMPLATES: '/masters/agreement-templates',

  // Properties — filtered views & lifecycle queues (property-lifecycle-plan.md §8)
  PROPERTIES_DRAFT: '/properties/drafts',
  PROPERTIES_ENRICHMENT: '/properties/enrichment',
  PROPERTIES_VERIFICATION: '/properties/verification',
  PROPERTIES_COMPLIANCE: '/properties/compliance-review',
  PROPERTIES_FINANCIAL_SETUP: '/properties/financial-setup',
  PROPERTIES_VASP: '/properties/vasp-onboarding',
  PROPERTIES_PIPELINE: '/properties/pipeline',
  PROPERTIES_CO_OWNER_KYC: '/properties/co-owner-kyc',
  PROPERTIES_REJECTED: '/properties/rejected',
  // Refresh queues (property-refresh-queues-plan.md). Operate on approved
  // properties only; surface attention without changing status.
  PROPERTIES_RENTAL_REFRESH: '/properties/rental-refresh',
  PROPERTIES_VALUATION_REFRESH: '/properties/valuation-refresh',
  // Post-approval money queue: pays collected rent out to token holders.

  // Sell requests (wire name: listing requests) — seller property-listing lifecycle. Own top-level module
  // (listing-requests-admin-api.md); moved out of `/properties/*` so each
  // lifecycle status can own a queue page the way properties' queues do.
  // Static queue paths outrank `/listing-requests/:id` in vue-router's
  // ranking, so no registration-order conflict.
  // Module root — redirects to the review queue (the default landing).
  LISTING_REQUESTS: '/listing-requests',
  // Work queues
  LISTING_REQUESTS_REVIEW: '/listing-requests/review',
  // Retired queue — owner verification is part of Review now. Kept as a
  // redirect target so bookmarks and in-flight links still land somewhere real.
  LEGACY_LISTING_REQUESTS_OWNER_VERIFICATION: '/listing-requests/owner-verification',
  LISTING_REQUESTS_AGREEMENTS: '/listing-requests/agreements',
  LISTING_REQUESTS_OWNERS_ONBOARDING: '/listing-requests/owners-onboarding',
  LISTING_REQUESTS_FINAL_APPROVAL: '/listing-requests/final-approval',
  // Where UAE Pass returns an admin after they counter-sign an agreement. Must
  // match seller-service's ESIGN_ADMIN_FINISH_CALLBACK_URL — that service builds
  // the callback, appending the agreement ids as query params.
  LISTING_REQUESTS_AGREEMENT_SIGN_RETURN: '/listing-requests/agreements/sign-return',
  // Awaiting seller
  LISTING_REQUESTS_REVISION_REQUESTED: '/listing-requests/revision-requested',
  // Directory
  LISTING_REQUESTS_ALL: '/listing-requests/all',
  LISTING_REQUESTS_PROMOTED: '/listing-requests/promoted',
  LISTING_REQUESTS_REJECTED: '/listing-requests/rejected',
  LISTING_REQUESTS_WITHDRAWN: '/listing-requests/withdrawn',
  // The record — every queue's drill-in, and the only surface that acts on a
  // request. `?tab=` selects the panel; `?from=` names the queue to return to.
  LISTING_REQUEST_DETAIL: '/listing-requests/:id',
  // Legacy paths — kept only to feed the redirect records in
  // `listingRequestsRoute.ts` so existing bookmarks survive the module move and
  // the agreements workspace becoming a tab. Never `router.push` these; use the
  // constants above.
  LEGACY_PROPERTIES_LISTING_REQUESTS: '/properties/listing-requests',
  LEGACY_PROPERTIES_LISTING_REQUEST_DETAIL: '/properties/listing-requests/:id',
  LEGACY_LISTING_REQUEST_AGREEMENTS_WORKSPACE: '/listing-requests/:id/agreements',

  // Investors — filtered views
  INVESTORS_KYC_PENDING: '/investors/kyc-pending',
  INVESTORS_ONBOARDING_REVIEW: '/investors/onboarding-review',
  INVESTORS_BANK_VERIFICATIONS: '/investors/bank-verifications',
  INVESTORS_VASP_ONBOARDING: '/investors/vasp-onboarding',
  INVESTORS_KYC_EXPIRED: '/investors/kyc-expired',
  INVESTORS_ALL: '/investors/all',
  INVESTORS_REJECTED: '/investors/rejected',
  INVESTORS_BLOCKED: '/investors/blocked',
  INVESTORS_PIPELINE: '/investors/pipeline',

  // Property owners — filtered views
  PROPERTY_OWNERS: '/property-owners',
  PROPERTY_OWNERS_PENDING: '/property-owners/pending',
  PROPERTY_OWNERS_KYC_REVIEW: '/property-owners/kyc-review',
  PROPERTY_OWNERS_BANK_VERIFICATIONS: '/property-owners/bank-verifications',
  PROPERTY_OWNERS_VASP: '/property-owners/vasp-onboarding',
  PROPERTY_OWNERS_KYC_EXPIRED: '/property-owners/kyc-expired',
  PROPERTY_OWNERS_PIPELINE: '/property-owners/pipeline',
  PROPERTY_OWNERS_ALL: '/property-owners/all',
  PROPERTY_OWNERS_DRAFT: '/property-owners/draft',
  PROPERTY_OWNERS_DRAFT_EDIT: '/property-owners/draft/edit/:id',
  PROPERTY_OWNERS_REJECTED: '/property-owners/rejected',
  PROPERTY_OWNERS_BLOCKED: '/property-owners/blocked',
  PROPERTY_OWNERS_CREATE: '/property-owners/create',
  PROPERTY_OWNERS_BANK_ACCOUNTS: '/property-owners/bank-accounts',
  PROPERTY_OWNERS_BANK_ACCOUNT_CREATE: '/property-owners/bank-accounts/create',

  // Treasury
  TREASURY_WITHDRAW_REQUESTS: '/treasury/withdraw-requests',
  TREASURY_DEPOSIT_REQUESTS: '/treasury/deposit-requests',
  TREASURY_FIAT_TRANSACTIONS_LIST: '/treasury/fiat-transactions',
  TREASURY_FIAT_TRANSACTIONS_DETAIL: '/treasury/fiat-transactions/:id',
  TREASURY_REFUND_REQUESTS: '/treasury/refund-requests',
  TREASURY_WALLETS_DIRECTORY: '/treasury/wallets',
  TREASURY_BANK_ACCOUNTS_DIRECTORY: '/treasury/bank-accounts',
  TREASURY_ACCOUNTS_DIRECTORY: '/treasury/accounts',

  // Rental Distribution — a money module, not a property one: it moves cash
  // into investor wallets. Was '/properties/rental-distributions'.
  RENTAL_DISTRIBUTION_RECEIPTS: '/rental-distributions/receipts',
  RENTAL_DISTRIBUTION_RUNS: '/rental-distributions/runs',
  RENTAL_DISTRIBUTION_LEDGER: '/rental-distributions/ledger',
  RENTAL_DISTRIBUTION_PAYOUTS: '/rental-distributions/payouts',

  // Other
  PROFILE: '/profile',
  HELP: '/help',
  ASK_SLICEY: '/ask-slicey',

  // Legal — public read-only viewers for the documents the Help module
  // publishes. `LEGAL_DOC` is the single route record; the three constants
  // below are the concrete paths the auth-page footers link to (see
  // `@modules/help`'s LEGAL_FOOTER_LINKS).
  LEGAL_DOC: '/legal/:docSlug',
  LEGAL_TERMS: '/legal/terms',
  LEGAL_PRIVACY: '/legal/privacy',
  LEGAL_REGULATORY: '/legal/regulatory',

  // System
  ERROR: '/error',
  NOTFOUND: '/not-found',
  LANDING: '/',
  SIGNUP: '/signup',
} as const

export type RoutePath = (typeof routes)[keyof typeof routes]

// Named-route constants. Use these with `router.push({ name })` instead of
// hand-typed strings — `router.push({ name: routeNames.ORDERS_ALL_DETAIL })`.
// Names must match the `name:` field on the corresponding route definition.
export const routeNames = {
  ORDERS_ALL_DETAIL: 'orders.all.detail',
  TREASURY_FIAT_TRANSACTIONS_DETAIL: 'treasury.fiatTransactions.detail',
} as const

export type RouteName = (typeof routeNames)[keyof typeof routeNames]
